import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useInitialData } from '../../services/localStorageContext';
import { Box, Button, Card, Tooltip } from '@mui/material';
import { useWebSocketConnection } from '../../services/connectionContext/connectionContext';
import { v4 } from 'uuid';
import { RawDataPlot } from '../plotlyPlotls/RawDataPlot';
import { TargetListPlot } from '../plotlyPlotls/TargetListPlot';
import { ValueListPlot } from '../plotlyPlotls/ValueListPlot';
import SerialInterface from '../serialInterface';
import RawDataAndRadarInterface from '../rawDataAndRadarInterface';
import RadarRecorder from '../radarRecorder';

const WebSocketAreas = React.memo(({ broker, sensor, area }) => {
    //console.log("render WebSocketAreaCard!");
    //console.log("WebSocketAreaCard Broker: ", broker, " with sensor: ", sensor, " and area ", area);
    const componentId = useRef(v4());
    const { getSocketDataByArea } = useInitialData();
    const { webSocketConnect, webSocketDisconnect, webSocketReconnect, eventEmitter } = useWebSocketConnection();
    const socketData = useMemo(
        () => getSocketDataByArea(broker, sensor, area),
        [getSocketDataByArea, broker, sensor, area]
    );
    const [webSocketState, setWebSocketState] = useState('closed');
    const { area_name, graphs, control_fields, socket_url } = socketData;
    const socketStateEvent = 'SOCKET_STATE:' + socket_url;

    useEffect(() => {
        const compId = componentId.current;
        console.log("WebSocketAreaCard recieved socket-URL: ", socket_url);
        webSocketConnect({ socketUrl: socket_url, automaticReconnect: false, componentId: compId });
        eventEmitter.on(socketStateEvent, setWebSocketState);
        return () => {
            webSocketDisconnect({ socketUrl: socket_url, componentId: compId });
            eventEmitter.off(socketStateEvent, setWebSocketState);
        }
    }, [webSocketConnect, broker, sensor, eventEmitter, webSocketDisconnect, socketStateEvent, socket_url]);

    const backgroundColor = useMemo(() => {
        switch (webSocketState) {
            case "opened":
                return "green";
            case "connecting":
                return "yellow";
            case "closed":
                return "red";
            default:
                return "red";
        }
    }, [webSocketState]);

    if (!socketData) {
        return null;
    }

    return (
        <>
            {/* Render WebSocketAreas */}
            < Card
                variant="outlined"
                sx={{ margin: '20px' }
                }
                key={`${broker}-${sensor}-${area}-socket_area_card`}>
                {/* Header section with sensor name and broker info */}
                <Box position="relative" padding={2}>
                    {/* Centered Sensor Name */}
                    <h3 style={{ margin: 0, textAlign: 'center' }}>
                        {area_name}
                    </h3>
                    {/* WebSocket Information aligned to the right */}
                    <div
                        style={{
                            position: 'absolute',
                            right: '16px', // Adjust this value to align with padding
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <h4 style={{ margin: 0 }}>{socket_url}</h4>
                        {/* Connection Status Dot */}
                        <Tooltip title={webSocketState} arrow>
                            <div
                                className="connection-status-indicator"
                                style={{ backgroundColor }}
                            ></div>
                        </Tooltip>
                    </div>
                </Box>

                <Button onClick={() => webSocketReconnect({ socketUrl: socket_url })}>
                    reconnect
                </Button>

                {
                    graphs.raw_data_plot_1 === "true" &&
                    <RawDataPlot
                        title="ADC Data"
                        socketRawDataEvent={'ON_MESSAGE' + socket_url + 'rawData'}
                        ylabel="LSB [-]"
                        xlabel="Samplenumber [#]"
                    />
                }

                {
                    graphs.raw_data_plot_2 === "true" &&
                    <RawDataPlot
                        title="Spectrum"
                        socketRawDataEvent={'ON_MESSAGE' + socket_url + 'specData'}
                        ylabel="FFT Value (up-ramp) [-]"
                        xlabel="distance [m]"
                    />
                }


                {
                    graphs.target_list_plot === "true" &&
                    <TargetListPlot
                        title="Targetlist (3 dominant targets)"
                        socketTargetsEvent={'ON_MESSAGE' + socket_url + 'targets'}
                    />
                }


                {
                    graphs.value_list_plot === "true" &&
                    <ValueListPlot
                        title="Magnitudes"
                        socketTargetsEvent={'ON_MESSAGE' + socket_url + 'targets'}
                    />
                }

                {
                    control_fields.serial_interface === "true" &&
                    <SerialInterface
                        title="Serial Interface"
                        socketUrl={socket_url}
                    />
                }

                {
                    control_fields.raw_data_and_radar_interface === "true" &&
                    <RawDataAndRadarInterface
                        title="Raw Data & Radar Interface"
                        socketUrl={socket_url}
                    />
                }

                {
                    control_fields.radar_recorder === "true" &&
                    <RadarRecorder
                        title="Recorder"
                        socketUrl={socket_url}
                    />
                }
            </Card >
        </>
    );
});

export default WebSocketAreas;